// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GuardRokIcon } from '../components/GuardRokLogo'
import ChatWidget from '../components/ChatWidget'
import LanguageSwitcher from '../components/LanguageSwitcher'
import { useLang } from '../i18n/LanguageContext'
import {
  Shield,
  ShieldOff,
  Eye,
  Lock,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Building2,
  Scale,
  Heart,
  Landmark,
  XCircle,
} from 'lucide-react'

const isTryDomain =
  typeof window !== 'undefined' && window.location.hostname.includes('tryguardrok')

const INDUSTRIES = [
  { icon: Building2, label: 'Financial services', regs: 'FINRA · SOX · PCI-DSS' },
  { icon: Heart, label: 'Healthcare', regs: 'HIPAA · HITECH · PHI' },
  { icon: Scale, label: 'Legal', regs: 'ABA Rules · Work Product' },
  { icon: Landmark, label: 'Government', regs: 'FedRAMP · FISMA · NIST' },
]

const WHAT_WE_BLOCK = [
  'Patient health information (PHI, MRN, diagnosis)',
  'Social Security & national ID numbers',
  'Credit card, bank & routing numbers',
  'Attorney-client privileged content',
  'Classified / CUI / FOUO markers',
  'Insider trading & material non-public info',
  'API keys, tokens & credentials',
  'Wire transfer & escrow instructions',
  'GDPR / LGPD personal data categories',
  'Custom company terms & codenames',
]

// Weekly-rotating risk stats — cycle every 7 days so the banner feels fresh
const RISK_STATS = [
  {
    stat: '1 in 3',
    text: 'employees admit to pasting sensitive company data into public AI tools, recent industry surveys consistently find.',
  },
  {
    stat: '$4.9M',
    text: 'global average cost of a data breach — IBM Cost of a Data Breach Report (regulated industries run higher).',
  },
  {
    stat: 'Rising',
    text: 'share of data-loss incidents now involve employees sharing information with AI tools — a top emerging risk for 2025.',
  },
  {
    stat: 'Most',
    text: 'employees now use AI tools on work devices — yet most organizations have no controls on what data is shared.',
  },
]
const _weekIdx = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000)) % RISK_STATS.length

// Industry scenarios for the interceptor demo
const SCENARIOS = [
  {
    industry: 'Healthcare',
    tool: 'chatgpt.com',
    tag: 'HIPAA · PHI',
    tagColor: '#3b82f6',
    raw: 'Summarise this discharge note for patient Maria Santos (DOB 03/14/1982, MRN 00482910). Diagnosis: Type 2 diabetes, prescribed metformin 500mg...',
    redacted:
      'Summarise this discharge note for patient ████████████ (DOB ██/██/████, MRN ████████). Diagnosis: ████████████, prescribed ██████████...',
    detection: 'PHI blocked — patient name, DOB, MRN, diagnosis redacted.',
  },
  {
    industry: 'Financial services',
    tool: 'copilot.microsoft.com',
    tag: 'FINRA · PCI-DSS',
    tagColor: '#22c55e',
    raw: 'Draft a memo on Q3 results for client Vanguard Capital. Account 8849201033, routing 021000021. Net position: $4.2M short on NVDA ahead of earnings...',
    redacted:
      'Draft a memo on Q3 results for client ████████████████. Account ██████████, routing █████████. Net position: ████ short on ████ ahead of earnings...',
    detection:
      'Blocked — account number, routing number, and non-public trading position detected.',
  },
  {
    industry: 'Legal',
    tool: 'claude.ai',
    tag: 'Attorney-Client Privilege',
    tagColor: '#a78bfa',
    raw: 'Privileged and confidential — summarise our litigation strategy for Hartwell v. Nexgen. Settlement floor is $2.1M. Do not disclose to opposing counsel...',
    redacted:
      '████████████████████████████ — summarise our litigation strategy for ████████████████. Settlement floor is ██████. Do not disclose to opposing counsel...',
    detection: 'Blocked — privileged communication marker and settlement terms detected.',
  },
  {
    industry: 'Government',
    tool: 'gemini.google.com',
    tag: 'FedRAMP · CUI',
    tagColor: '#f59e0b',
    raw: 'Translate this briefing — FOR OFFICIAL USE ONLY. Contract W911NF-24-C-0082, contractor: Lockheed Martin. System clearance level: SECRET. NOFORN...',
    redacted:
      'Translate this briefing — ████████████████████. Contract ████████████████████, contractor: ████████████████. System clearance level: ██████. ██████...',
    detection: 'Blocked — FOUO marker, contract number, clearance level, and NOFORN tag detected.',
  },
  {
    industry: 'HR',
    tool: 'perplexity.ai',
    tag: 'PII · HR Data',
    tagColor: '#10b981',
    raw: 'Employee Sarah Chen, SSN 421-67-8901, salary $145,000, placed on PIP for missing targets three quarters running. Draft her termination letter...',
    redacted:
      'Employee ████████████, SSN ███-██-████, salary ████████, placed on PIP for missing targets three quarters running. Draft her termination letter...',
    detection: 'Blocked — SSN and employee salary detected.',
  },
  {
    industry: 'IT / DevOps',
    tool: 'grok.com',
    tag: 'Credentials',
    tagColor: '#ef4444',
    raw: 'Here is my AWS access key AKIAIOSFODNN7EXAMPLE and secret wJalrXUtnFEMI/K7MDENG. Help me debug this Lambda function timeout...',
    redacted:
      'Here is my AWS access key ████████████████████████ and secret ██████████████████████. Help me debug this Lambda function timeout...',
    detection: 'Blocked — AWS access key and secret credential detected.',
  },
  {
    industry: 'Pharma',
    tool: 'chat.mistral.ai',
    tag: 'Trade Secret · IP',
    tagColor: '#f472b6',
    raw: 'INTERNAL ONLY — our compound VX-2291 achieves 94% binding affinity vs competitor 67%. Do not share externally. Summarise for the board deck...',
    redacted:
      '████████████ — our compound ████████ achieves ██% binding affinity vs competitor ██%. Do not share externally. Summarise for the board deck...',
    detection: 'Blocked — confidential marker and proprietary compound data detected.',
  },
  {
    industry: 'Banking',
    tool: 'chat.deepseek.com',
    tag: 'SOX · PCI-DSS',
    tagColor: '#06b6d4',
    raw: 'Wire $2.4M to beneficiary account 9876543210, routing 021000021, Chase Bank. Reference: Project Falcon acquisition escrow. Authorised by CFO...',
    redacted:
      'Wire ██████ to beneficiary account ██████████, routing █████████, ████ ████. Reference: ████████████████████████. Authorised by CFO...',
    detection: 'Blocked — wire transfer details, account and routing numbers detected.',
  },
]

// 3 animation phases per scenario: idle → scanning → blocked
type Phase = 0 | 1 | 2

// Start on a different industry each week so repeat visitors see fresh content
const WEEKLY_START = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000)) % SCENARIOS.length

function InterceptorDemo() {
  const [scenarioIdx, setScenarioIdx] = useState(WEEKLY_START)
  const [phase, setPhase] = useState<Phase>(0)
  const [auto, setAuto] = useState(true)

  useEffect(() => {
    if (!auto) return
    const TIMINGS: Record<Phase, number> = { 0: 1200, 1: 1000, 2: 2800 }
    const t = setTimeout(() => {
      if (phase < 2) {
        setPhase(p => (p + 1) as Phase)
      } else {
        setPhase(0)
        setScenarioIdx(i => (i + 1) % SCENARIOS.length)
      }
    }, TIMINGS[phase])
    return () => clearTimeout(t)
  }, [auto, phase, scenarioIdx])

  const s = SCENARIOS[scenarioIdx]
  const isBlocked = phase === 2
  const isScanning = phase === 1

  return (
    <div
      style={{
        background: '#050d1a',
        border: '1px solid #1e293b',
        borderRadius: 16,
        padding: 24,
        fontFamily: 'monospace',
      }}
      onMouseEnter={() => setAuto(false)}
      onMouseLeave={() => setAuto(true)}
    >
      {/* Industry tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' as const }}>
        {SCENARIOS.map((sc, i) => (
          <button
            key={sc.industry}
            onClick={() => {
              setScenarioIdx(i)
              setPhase(0)
              setAuto(false)
            }}
            style={{
              fontSize: 11,
              fontFamily: 'system-ui',
              fontWeight: 600,
              padding: '3px 10px',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.15s',
              background: i === scenarioIdx ? sc.tagColor + '22' : '#0f172a',
              color: i === scenarioIdx ? sc.tagColor : '#475569',
              outline: i === scenarioIdx ? `1px solid ${sc.tagColor}44` : '1px solid #1e293b',
            }}
          >
            {sc.industry}
          </button>
        ))}
      </div>

      {/* Browser bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 5 }}>
          {['#ff5f57', '#febc2e', '#28c840'].map(c => (
            <div key={c} style={{ width: 9, height: 9, borderRadius: '50%', background: c }} />
          ))}
        </div>
        <div
          style={{
            flex: 1,
            background: '#0f172a',
            borderRadius: 6,
            padding: '4px 10px',
            fontSize: 11,
            color: '#475569',
          }}
        >
          {s.tool}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            background: isBlocked ? '#1a0a0a' : isScanning ? '#0f1a0a' : '#0f172a',
            border: `1px solid ${isBlocked ? '#7f1d1d' : isScanning ? '#14532d' : '#1e293b'}`,
            borderRadius: 6,
            padding: '3px 9px',
          }}
        >
          <div
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: isBlocked ? '#ef4444' : isScanning ? '#22c55e' : '#6b7280',
              boxShadow: isScanning ? '0 0 6px #22c55e' : 'none',
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontFamily: 'system-ui',
              color: isBlocked ? '#ef4444' : isScanning ? '#22c55e' : '#6b7280',
            }}
          >
            {isBlocked ? 'BLOCKED' : isScanning ? 'SCANNING' : 'GuardRok'}
          </span>
        </div>
      </div>

      {/* Regulation tag */}
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: s.tagColor + '15',
          border: `1px solid ${s.tagColor}33`,
          borderRadius: 5,
          padding: '2px 8px',
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 10, color: s.tagColor, fontFamily: 'system-ui', fontWeight: 600 }}>
          {s.tag}
        </span>
      </div>

      {/* Prompt */}
      <div
        style={{
          background: '#0a0f1a',
          border: `1px solid ${isBlocked ? '#7f1d1d' : '#1e293b'}`,
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 14,
          minHeight: 76,
          transition: 'border-color 0.3s',
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: isBlocked ? '#fca5a5' : '#94a3b8',
            lineHeight: 1.65,
            transition: 'color 0.3s',
          }}
        >
          {isBlocked ? s.redacted : s.raw}
        </div>
      </div>

      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {isBlocked ? (
          <>
            <XCircle size={14} color="#ef4444" style={{ flexShrink: 0 }} />
            <span
              style={{ fontSize: 11, color: '#ef4444', fontFamily: 'system-ui', lineHeight: 1.4 }}
            >
              {s.detection}
            </span>
          </>
        ) : isScanning ? (
          <>
            <div
              style={{
                width: 13,
                height: 13,
                border: '2px solid #22c55e',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 11, color: '#22c55e', fontFamily: 'system-ui' }}>
              Scanning for sensitive data patterns…
            </span>
          </>
        ) : (
          <>
            <div
              style={{
                width: 7,
                height: 7,
                background: '#475569',
                borderRadius: '50%',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'system-ui' }}>
              Waiting for prompt…
            </span>
          </>
        )}
      </div>

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: 5, justifyContent: 'center', marginTop: 18 }}>
        {SCENARIOS.map((_, i) => (
          <button
            key={i}
            onClick={() => {
              setScenarioIdx(i)
              setPhase(0)
              setAuto(false)
            }}
            style={{
              width: i === scenarioIdx ? 18 : 6,
              height: 6,
              borderRadius: 3,
              background: i === scenarioIdx ? '#E63946' : '#1e293b',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              padding: 0,
            }}
          />
        ))}
      </div>
    </div>
  )
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string

function LeadCaptureForm() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [msg, setMsg] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setMsg('Enter a valid work email.')
      setStatus('error')
      return
    }
    setStatus('loading')
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/lead-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source: 'landing-cta' }),
      })
      const data = await res.json()
      if (data.ok) {
        setStatus('done')
        setMsg("You're on the list — we'll reach out shortly.")
      } else {
        setStatus('error')
        setMsg(data.error ?? 'Something went wrong.')
      }
    } catch {
      setStatus('error')
      setMsg('Network error — please try again.')
    }
  }

  if (status === 'done') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: '#22c55e',
          fontWeight: 600,
          fontSize: 14,
          marginBottom: 8,
        }}
      >
        <CheckCircle size={16} />
        {msg}
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        display: 'flex',
        gap: 8,
        justifyContent: 'center',
        flexWrap: 'wrap' as const,
        marginBottom: 8,
      }}
    >
      <input
        type="email"
        placeholder="Work email"
        value={email}
        onChange={e => {
          setEmail(e.target.value)
          setStatus('idle')
          setMsg('')
        }}
        required
        style={{
          padding: '12px 16px',
          borderRadius: 10,
          border: status === 'error' ? '1px solid #E63946' : '1px solid #374151',
          background: '#0f172a',
          color: '#f1f5f9',
          fontSize: 14,
          width: 240,
          outline: 'none',
        }}
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        style={{
          padding: '12px 20px',
          borderRadius: 10,
          border: 'none',
          background: status === 'loading' ? '#7f1d1d' : '#E63946',
          color: '#fff',
          fontWeight: 700,
          fontSize: 14,
          cursor: status === 'loading' ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {status === 'loading' ? (
          'Sending…'
        ) : (
          <>
            Get early access <ArrowRight size={14} />
          </>
        )}
      </button>
      {status === 'error' && msg && (
        <div
          style={{
            width: '100%',
            textAlign: 'center',
            color: '#E63946',
            fontSize: 13,
            marginTop: 4,
          }}
        >
          {msg}
        </div>
      )}
    </form>
  )
}

function WebinarNotifyForm() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')
  const [msg, setMsg] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = email.trim().toLowerCase()
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setMsg('Enter a valid work email.')
      setStatus('error')
      return
    }
    setStatus('loading')
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/lead-capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed, source: 'webinar-notify' }),
      })
      const data = await res.json()
      if (data.ok) {
        setStatus('done')
        setMsg("You're on the list — we'll send you the details.")
      } else {
        setStatus('error')
        setMsg(data.error ?? 'Something went wrong.')
      }
    } catch {
      setStatus('error')
      setMsg('Network error — please try again.')
    }
  }

  if (status === 'done') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: '#a5b4fc',
          fontWeight: 600,
          fontSize: 13,
        }}
      >
        <CheckCircle size={15} />
        {msg}
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column' as const, gap: 8, alignItems: 'center' }}
    >
      <input
        type="email"
        placeholder="Work email — notify me"
        value={email}
        onChange={e => {
          setEmail(e.target.value)
          setStatus('idle')
          setMsg('')
        }}
        required
        style={{
          padding: '11px 16px',
          borderRadius: 8,
          border: status === 'error' ? '1px solid #E63946' : '1px solid rgba(99,102,241,0.3)',
          background: '#0a0f1e',
          color: '#f1f5f9',
          fontSize: 13,
          width: 230,
          outline: 'none',
        }}
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        style={{
          width: '100%',
          padding: '11px 20px',
          borderRadius: 8,
          border: 'none',
          background: status === 'loading' ? '#4338ca' : '#6366f1',
          color: '#fff',
          fontWeight: 700,
          fontSize: 13,
          cursor: status === 'loading' ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
        }}
      >
        {status === 'loading' ? (
          'Registering…'
        ) : (
          <>
            Notify me when it's live <ArrowRight size={13} />
          </>
        )}
      </button>
      {status === 'error' && msg && (
        <div style={{ color: '#E63946', fontSize: 12, marginTop: 2 }}>{msg}</div>
      )}
    </form>
  )
}

export default function Landing() {
  const { T } = useLang()
  const riskStat = RISK_STATS[_weekIdx]

  const FEATURES = [
    { icon: Heart, color: '#ef4444', title: T.feat_phi, body: T.feat_phi_desc },
    { icon: Eye, color: '#6366f1', title: T.feat_pii, body: T.feat_pii_desc },
    { icon: Scale, color: '#22c55e', title: T.feat_financial, body: T.feat_financial_desc },
    { icon: Lock, color: '#f59e0b', title: T.feat_api, body: T.feat_api_desc },
    { icon: Landmark, color: '#14b8a6', title: T.feat_real_estate, body: T.feat_real_estate_desc },
    { icon: Shield, color: '#94a3b8', title: T.feat_confidential, body: T.feat_confidential_desc },
  ]

  const heroTitle = isTryDomain ? (
    <>
      {T.hero_cta_primary ? T.hero_h1_line1 : 'Try GuardRok free —'}
      <br />
      <span style={{ color: '#E63946' }}>{T.hero_h1_line2}</span>
    </>
  ) : (
    <>
      {T.hero_h1_line1}
      <br />
      <span style={{ color: '#E63946' }}>{T.hero_h1_line2}</span>
    </>
  )

  const heroSub = isTryDomain ? T.hero_social_proof : T.hero_subtitle

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#030712',
        color: '#fff',
        fontFamily: 'system-ui, sans-serif',
        overflowX: 'hidden',
      }}
    >
      {/* Nav */}
      <nav
        className="gr-nav"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #0f172a',
          position: 'sticky',
          top: 0,
          background: 'rgba(3,9,18,0.92)',
          backdropFilter: 'blur(12px)',
          zIndex: 100,
        }}
      >
        <Link
          to="/"
          style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}
        >
          <GuardRokIcon size={36} />
          <div>
            <div
              style={{
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: '-0.5px',
                lineHeight: 1,
                color: '#fff',
              }}
            >
              Guard<span style={{ color: '#E63946' }}>Rok</span>
              <span
                style={{ fontSize: 8, color: '#6b7280', verticalAlign: 'super', marginLeft: 1 }}
              >
                ™
              </span>
              {isTryDomain && (
                <span
                  style={{
                    fontSize: 11,
                    background: '#E63946',
                    color: '#fff',
                    borderRadius: 4,
                    padding: '1px 6px',
                    marginLeft: 8,
                    fontWeight: 600,
                    verticalAlign: 'middle',
                  }}
                >
                  TRY
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 9,
                letterSpacing: '3px',
                color: '#6b7280',
                textTransform: 'uppercase' as const,
                marginTop: 2,
              }}
            >
              AI Firewall
            </div>
          </div>
        </Link>
        <div className="gr-nav-links" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Link to="/demo" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            Request Demo
          </Link>
          <Link to="/login" style={{ color: '#9ca3af', fontSize: 14, textDecoration: 'none' }}>
            {T.nav_signin}
          </Link>
          {isTryDomain ? (
            <Link
              to="/signup"
              style={{
                background: '#E63946',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                padding: '8px 18px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              {T.nav_start_free}
            </Link>
          ) : (
            <Link
              to="/signup"
              style={{
                background: '#E63946',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                padding: '8px 18px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              {T.nav_start_free}
            </Link>
          )}
          <LanguageSwitcher />
        </div>
        <div className="gr-nav-cta-mobile">
          {isTryDomain ? (
            <Link
              to="/signup"
              style={{
                background: '#E63946',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                padding: '8px 16px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              {T.nav_start_free}
            </Link>
          ) : (
            <Link
              to="/signup"
              style={{
                background: '#E63946',
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                padding: '8px 16px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              {T.nav_start_free}
            </Link>
          )}
          <LanguageSwitcher />
        </div>
      </nav>

      {/* Hero */}
      <div className="gr-hero" style={{ maxWidth: 1100, margin: '0 auto', alignItems: 'center' }}>
        <div>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#0f172a',
              border: '1px solid #1e293b',
              borderRadius: 20,
              padding: '6px 14px',
              marginBottom: 24,
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                background: '#22c55e',
                borderRadius: '50%',
                boxShadow: '0 0 6px #22c55e',
              }}
            />
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{T.hero_badge}</span>
          </div>
          <h1
            className="gr-hero-title"
            style={{ fontWeight: 900, letterSpacing: '-2px', lineHeight: 1.1, marginBottom: 14 }}
          >
            {heroTitle}
          </h1>
          <p
            style={{
              fontSize: 13,
              fontStyle: 'italic',
              color: '#E63946',
              letterSpacing: '0.04em',
              marginBottom: 14,
              fontWeight: 500,
            }}
          >
            Use any AI. Share nothing.
          </p>
          <p style={{ fontSize: 16, color: '#94a3b8', lineHeight: 1.7, marginBottom: 32 }}>
            {heroSub}
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' as const }}>
            {isTryDomain ? (
              <>
                <Link
                  to="/signup"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#E63946',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 15,
                    padding: '13px 24px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    boxShadow: '0 0 24px rgba(230,57,70,0.35)',
                  }}
                >
                  {T.hero_cta_primary} <ArrowRight size={16} />
                </Link>
                <Link
                  to="/signup"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#0f172a',
                    border: '1px solid #1e293b',
                    color: '#e2e8f0',
                    fontWeight: 600,
                    fontSize: 15,
                    padding: '13px 24px',
                    borderRadius: 10,
                    textDecoration: 'none',
                  }}
                >
                  {T.nav_start_free}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/signup"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#E63946',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 15,
                    padding: '13px 24px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    boxShadow: '0 0 24px rgba(230,57,70,0.35)',
                  }}
                >
                  {T.nav_start_free} <ArrowRight size={16} />
                </Link>
                <Link
                  to="/demo"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#0f172a',
                    border: '1px solid #475569',
                    color: '#f1f5f9',
                    fontWeight: 700,
                    fontSize: 15,
                    padding: '13px 24px',
                    borderRadius: 10,
                    textDecoration: 'none',
                  }}
                >
                  Request Demo →
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Live interceptor demo */}
        <InterceptorDemo />
      </div>

      {/* Video section — drop your video URL below when ready */}
      {/* To activate: set VIDEO_URL to your YouTube/Vimeo/mp4 URL */}
      {(() => {
        const VIDEO_URL: string = '' // ← paste your video URL here when ready
        if (!VIDEO_URL) return null
        const isYouTube = VIDEO_URL.includes('youtube') || VIDEO_URL.includes('youtu.be')
        const isVimeo = VIDEO_URL.includes('vimeo')
        const embedUrl = isYouTube
          ? VIDEO_URL.replace('watch?v=', 'embed/').replace('youtu.be/', 'www.youtube.com/embed/') +
            '?autoplay=0&rel=0'
          : isVimeo
            ? VIDEO_URL.replace('vimeo.com/', 'player.vimeo.com/video/')
            : null
        return (
          <div
            className="gr-section"
            style={{ maxWidth: 900, margin: '0 auto 72px', textAlign: 'center' as const }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#475569',
                letterSpacing: '2px',
                textTransform: 'uppercase' as const,
                marginBottom: 14,
              }}
            >
              See it in action
            </div>
            <h2
              style={{
                fontSize: 26,
                fontWeight: 900,
                color: '#f1f5f9',
                letterSpacing: '-0.5px',
                marginBottom: 24,
              }}
            >
              Watch GuardRok stop a data leak in real time
            </h2>
            <div
              style={{
                position: 'relative' as const,
                paddingBottom: '56.25%',
                height: 0,
                borderRadius: 14,
                overflow: 'hidden',
                border: '1px solid #1e293b',
                boxShadow: '0 0 40px rgba(230,57,70,0.12)',
              }}
            >
              {embedUrl ? (
                <iframe
                  src={embedUrl}
                  title="GuardRok Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute' as const,
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              ) : (
                <video
                  src={VIDEO_URL}
                  controls
                  style={{
                    position: 'absolute' as const,
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              )}
            </div>
          </div>
        )
      })()}

      {/* Scrolling org strip — representative target segments, not named customers */}
      <div
        style={{
          overflow: 'hidden',
          borderTop: '1px solid #0f172a',
          borderBottom: '1px solid #0f172a',
          background: '#050810',
          padding: '14px 0',
          marginBottom: 0,
        }}
      >
        <div
          style={{
            textAlign: 'center' as const,
            fontSize: 11,
            color: '#475569',
            fontWeight: 600,
            letterSpacing: '1.5px',
            textTransform: 'uppercase' as const,
            marginBottom: 10,
          }}
        >
          Built for organizations like these
        </div>
        <div
          style={{
            display: 'flex',
            animation: 'marquee 28s linear infinite',
            width: 'max-content',
          }}
        >
          {[...Array(2)]
            .flatMap(() => [
              'Regional Health System · Texas',
              'Boutique Asset Manager · New York',
              'AmLaw 200 Firm',
              'Federal Contractor · D.C.',
              'Regional Bank · Florida',
              'K-12 School District · Ohio',
              'Specialty Pharma · New Jersey',
              'Municipal Government · California',
              'Mid-Market Insurance Carrier',
              'Defense Subcontractor · Virginia',
            ])
            .map((name, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 28,
                  paddingRight: 28,
                  whiteSpace: 'nowrap' as const,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: '#334155',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  }}
                >
                  {name}
                </span>
                <span
                  style={{
                    width: 3,
                    height: 3,
                    borderRadius: '50%',
                    background: '#1e293b',
                    display: 'inline-block',
                    flexShrink: 0,
                  }}
                />
              </div>
            ))}
        </div>
      </div>

      {/* Risk banner — rotates weekly */}
      <div className="gr-section" style={{ maxWidth: 1100, margin: '0 auto 72px' }}>
        <div
          style={{
            background: '#1a0a0a',
            border: '1px solid #7f1d1d',
            borderRadius: 12,
            padding: '18px 24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 14,
          }}
        >
          <AlertTriangle size={20} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 14, color: '#fca5a5', lineHeight: 1.6 }}>
            <strong style={{ fontSize: 18, color: '#ef4444', marginRight: 8 }}>
              {riskStat.stat}
            </strong>
            {riskStat.text}
            <span
              style={{
                display: 'block',
                marginTop: 8,
                fontSize: 13,
                color: '#991b1b',
                borderTop: '1px solid #450a0a',
                paddingTop: 8,
              }}
            >
              One mis-pasted prompt can trigger a HIPAA breach, FINRA violation, or privilege waiver
              — GuardRok stops it before it sends.
            </span>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="gr-section" style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 80 }}>
        <h2
          style={{
            fontSize: 28,
            fontWeight: 800,
            textAlign: 'center' as const,
            marginBottom: 40,
            letterSpacing: '-0.5px',
          }}
        >
          {T.features_title}
        </h2>
        <div className="gr-grid-2" style={{ display: 'grid', gap: 18 }}>
          {FEATURES.map(({ icon: Icon, color, title, body }) => (
            <div
              key={title}
              style={{
                background: '#0a0f1a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: '26px 26px',
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  background: '#0f172a',
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                  border: `1px solid ${color}22`,
                }}
              >
                <Icon size={20} color={color} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 8 }}>
                {title}
              </div>
              <div style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.65 }}>{body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* What we block */}
      <div
        className="gr-pad-section"
        style={{
          background: '#05080f',
          borderTop: '1px solid #0f172a',
          borderBottom: '1px solid #0f172a',
        }}
      >
        <div
          className="gr-block-grid"
          style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', alignItems: 'center' }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                color: '#E63946',
                fontWeight: 700,
                letterSpacing: '2px',
                textTransform: 'uppercase' as const,
                marginBottom: 12,
              }}
            >
              What GuardRok blocks
            </div>
            <h2
              style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 14 }}
            >
              Every sensitive data type, covered
            </h2>
            <p style={{ color: '#94a3b8', fontSize: 15, lineHeight: 1.7 }}>
              Pre-built detection for the patterns that trigger regulators — plus custom keywords
              for your own proprietary data.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
            {WHAT_WE_BLOCK.map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <CheckCircle size={15} color="#22c55e" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: 14, color: '#cbd5e1' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industries */}
      <div className="gr-pad-section" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <h2
          style={{
            fontSize: 26,
            fontWeight: 800,
            textAlign: 'center' as const,
            marginBottom: 10,
            letterSpacing: '-0.5px',
          }}
        >
          Built for regulated industries
        </h2>
        <p
          style={{ color: '#94a3b8', fontSize: 15, textAlign: 'center' as const, marginBottom: 40 }}
        >
          Pre-built compliance packs — install in one click.
        </p>
        <div className="gr-grid-4" style={{ display: 'grid', gap: 16 }}>
          {INDUSTRIES.map(({ icon: Icon, label, regs }) => (
            <div
              key={label}
              style={{
                background: '#0a0f1a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: '26px 20px',
                textAlign: 'center' as const,
              }}
            >
              <div
                style={{
                  width: 46,
                  height: 46,
                  background: '#0f172a',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 12px',
                  border: '1px solid #1e293b',
                }}
              >
                <Icon size={20} color="#E63946" />
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', marginBottom: 5 }}>
                {label}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>{regs}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Trust bar */}
      <div
        className="gr-trust-bar"
        style={{
          background: '#05080f',
          borderTop: '1px solid #0f172a',
          borderBottom: '1px solid #0f172a',
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 40,
            flexWrap: 'wrap' as const,
          }}
        >
          {[
            { label: T.stat_block, sub: T.stat_block_label },
            { label: T.stat_tools, sub: T.stat_tools_label },
            { label: T.stat_store, sub: T.stat_store_label },
            { label: T.stat_privacy, sub: T.stat_privacy_label },
          ].map(({ label, sub }) => (
            <div key={label} style={{ textAlign: 'center' as const }}>
              <div
                style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', letterSpacing: '0.3px' }}
              >
                {label}
              </div>
              <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Zero-Knowledge Identity */}
      <div
        style={{
          background: '#08111e',
          borderTop: '1px solid #0f172a',
          borderBottom: '1px solid #0f172a',
          padding: '64px 48px',
        }}
      >
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#E63946', letterSpacing: '2px', textTransform: 'uppercase' as const, marginBottom: 12 }}>
              Privacy by design
            </div>
            <h2 style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 16 }}>
              We never know<br />
              <span style={{ color: '#E63946' }}>who your employees are</span>
            </h2>
            <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.75, marginBottom: 20 }}>
              Every user gets a randomly assigned anonymous GR-ID. Your audit logs show
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}> GR-4821 triggered a PHI policy</span> —
              not a name. The identity mapping lives only in your HR system.
              GuardRok's servers never receive employee names or emails.
            </p>
            <p style={{ fontSize: 14, color: '#94a3b8', lineHeight: 1.75, marginBottom: 24 }}>
              This isn't a policy decision. It's architectural. A breach of GuardRok cannot expose your employees' identities.
            </p>
            <Link
              to="/security"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
                color: '#E63946',
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              Read how it works → Trust Center
            </Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 14 }}>
            {[
              { label: 'GDPR by design', desc: 'No personal data in analytics — compliant without configuration' },
              { label: 'Works-council friendly', desc: 'Employer holds the identity key, not the vendor' },
              { label: 'CISO-level trust', desc: 'Vendor breach cannot expose employee identities' },
              { label: 'BAA & DPA available', desc: 'Signed within 24 hours for healthcare and EU orgs' },
            ].map(({ label, desc }) => (
              <div
                key={label}
                style={{
                  background: '#0d1117',
                  border: '1px solid #1e293b',
                  borderRadius: 12,
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                }}
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#22c55e',
                    flexShrink: 0,
                    marginTop: 5,
                  }}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Credential badges */}
      <div
        className="gr-section"
        style={{ borderBottom: '1px solid #0f172a', paddingTop: 14, paddingBottom: 14 }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            flexWrap: 'wrap' as const,
          }}
        >
          {[
            { icon: '🔐', label: 'SOC 2 Type I', sub: 'Audit in progress' },
            { icon: '🏥', label: 'BAA · DPA available', sub: 'Signed within 24 hours' },
            { icon: '🪪', label: 'Zero-Knowledge Identity', sub: 'We never know employee names' },
            { icon: '⚖️', label: 'Patent Pending', sub: 'USPTO filed 2026' },
            { icon: '🌐', label: 'Browser AI Firewall', sub: 'No IT setup · Deploys in 10 min' },
          ].map(({ icon, label, sub }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>{icon}</span>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#cbd5e1',
                    letterSpacing: '0.2px',
                  }}
                >
                  {label}
                </div>
                <div style={{ fontSize: 10, color: '#475569' }}>{sub}</div>
              </div>
            </div>
          ))}
          <Link
            to="/security"
            style={{
              fontSize: 11,
              color: '#E63946',
              textDecoration: 'none',
              fontWeight: 600,
              borderLeft: '1px solid #1e293b',
              paddingLeft: 20,
            }}
          >
            Trust Center →
          </Link>
        </div>
      </div>

      {/* Testimonials */}
      <div
        className="gr-pad-section"
        style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 80 }}
      >
        <div
          style={{
            fontSize: 12,
            color: '#E63946',
            fontWeight: 700,
            letterSpacing: '2px',
            textTransform: 'uppercase' as const,
            textAlign: 'center' as const,
            marginBottom: 10,
          }}
        >
          Real scenarios. Real risks.
        </div>
        <h2
          style={{
            fontSize: 26,
            fontWeight: 800,
            textAlign: 'center' as const,
            marginBottom: 10,
            letterSpacing: '-0.5px',
          }}
        >
          The situations GuardRok is built to stop
        </h2>
        <p
          style={{ color: '#64748b', fontSize: 14, textAlign: 'center' as const, marginBottom: 48 }}
        >
          These aren't edge cases. They're happening in every regulated organisation that uses AI tools today.
        </p>
        <div className="gr-grid-3" style={{ display: 'grid', gap: 20 }}>
          {[
            {
              scenario: 'Discharge summary pasted into ChatGPT',
              quote:
                'A nurse pastes a full discharge summary — patient name, MRN, medications — into ChatGPT to draft a care summary. GuardRok redacts the PHI before the prompt is ever sent.',
              segment: 'Healthcare',
              tag: 'HIPAA',
            },
            {
              scenario: 'Non-public trading data in a Copilot memo',
              quote:
                'An analyst uses Copilot to draft a client memo that includes a non-public trading position. GuardRok detects the material non-public information and blocks it at the source — before the model sees it.',
              segment: 'Financial services',
              tag: 'FINRA',
            },
            {
              scenario: 'Privileged litigation strategy in Claude',
              quote:
                "An associate asks Claude to summarize a privileged litigation strategy, including settlement terms. GuardRok's attorney-client detection catches the privilege marker — no IT ticket or VPN required.",
              segment: 'Legal',
              tag: 'ABA Rules',
            },
            {
              scenario: 'CUI in a government briefing',
              quote:
                'A contractor pastes a FOUO briefing with a contract number and clearance level into Gemini. GuardRok blocks it without rerouting any network traffic — workable on hybrid government devices.',
              segment: 'Government',
              tag: 'FedRAMP · CUI',
            },
            {
              scenario: 'Card and account data in a prompt',
              quote:
                'An employee drops account and routing numbers into an AI tool to reconcile a statement. GuardRok redacts the PCI data inline and logs the attempt for the audit trail.',
              segment: 'Banking',
              tag: 'PCI-DSS',
            },
            {
              scenario: 'Student records in an essay-grading prompt',
              quote:
                'A teacher uses an AI tool to grade essays and includes student names and scores. GuardRok catches the FERPA-protected data automatically before it leaves the browser.',
              segment: 'Education',
              tag: 'FERPA',
            },
          ].map(({ scenario, quote, segment, tag }) => (
            <div
              key={scenario}
              style={{
                background: '#0a0f1a',
                border: '1px solid #1e293b',
                borderRadius: 14,
                padding: '24px 22px',
                display: 'flex',
                flexDirection: 'column' as const,
                gap: 16,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.4 }}>
                {scenario}
              </div>
              <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.75, flex: 1 }}>
                {quote}
              </div>
              <div
                style={{
                  borderTop: '1px solid #1e293b',
                  paddingTop: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>{segment}</span>
                <span
                  style={{
                    display: 'inline-block',
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#E63946',
                    background: '#E6394611',
                    border: '1px solid #E6394622',
                    borderRadius: 4,
                    padding: '2px 8px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {tag}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Design partner CTA */}
        <div
          style={{
            marginTop: 48,
            background: '#0d1117',
            border: '1px solid rgba(230,57,70,0.2)',
            borderRadius: 14,
            padding: '28px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap' as const,
            gap: 20,
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 4 }}>
              Recognise these scenarios in your organisation?
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
              We're looking for 3 design partners in healthcare, legal, and financial services.
              Founding pricing · direct line to the product team · shape what gets built next.
            </div>
          </div>
          <a
            href="mailto:hello@guardrok.com?subject=Design Partner Interest"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#E63946',
              color: '#fff',
              fontWeight: 700,
              fontSize: 13,
              padding: '12px 22px',
              borderRadius: 10,
              textDecoration: 'none',
              whiteSpace: 'nowrap' as const,
            }}
          >
            Become a design partner →
          </a>
        </div>
      </div>

      {/* Webinar section */}
      {(() => {
        const WEBINAR_URL = '' // ← paste your webinar registration URL here when ready
        return (
          <div className="gr-section" style={{ maxWidth: 1100, margin: '0 auto 72px' }}>
            <div
              style={{
                background: 'linear-gradient(135deg, #0a0f1e 0%, #0f1a2e 100%)',
                border: '1px solid rgba(99,102,241,0.3)',
                borderRadius: 16,
                padding: '32px 40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 32,
                flexWrap: 'wrap' as const,
                position: 'relative' as const,
                overflow: 'hidden' as const,
              }}
            >
              {/* Glow */}
              <div
                style={{
                  position: 'absolute',
                  top: -60,
                  right: -60,
                  width: 240,
                  height: 240,
                  background: 'rgba(99,102,241,0.06)',
                  borderRadius: '50%',
                  pointerEvents: 'none' as const,
                }}
              />
              <div style={{ flex: 1, minWidth: 280 }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    background: 'rgba(99,102,241,0.1)',
                    border: '1px solid rgba(99,102,241,0.25)',
                    borderRadius: 20,
                    padding: '4px 14px',
                    marginBottom: 14,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#a5b4fc',
                      letterSpacing: '1px',
                      textTransform: 'uppercase' as const,
                    }}
                  >
                    {WEBINAR_URL ? '🎙 Live Webinar' : '🎙 Coming Soon'}
                  </span>
                </div>
                <h2
                  style={{
                    fontSize: 22,
                    fontWeight: 900,
                    color: '#f1f5f9',
                    letterSpacing: '-0.5px',
                    marginBottom: 10,
                    lineHeight: 1.3,
                  }}
                >
                  How to Stop AI Data Leaks Before They Happen
                </h2>
                <p
                  style={{
                    fontSize: 14,
                    color: '#64748b',
                    lineHeight: 1.7,
                    marginBottom: 0,
                    maxWidth: 480,
                  }}
                >
                  A live walkthrough of how regulated teams — law firms, healthcare providers,
                  financial services — are governing AI usage without slowing employees down. Real
                  scenarios. Real demos. Q&A included.
                </p>
                <div style={{ display: 'flex', gap: 20, marginTop: 14, flexWrap: 'wrap' as const }}>
                  {[
                    'AI governance for compliance teams',
                    'Live GuardRok demo',
                    'Q&A with founders',
                    'Free to attend',
                  ].map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          background: '#6366f1',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ textAlign: 'center' as const, flexShrink: 0 }}>
                {WEBINAR_URL ? (
                  <a
                    href={WEBINAR_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      background: '#6366f1',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 14,
                      padding: '13px 24px',
                      borderRadius: 10,
                      textDecoration: 'none',
                    }}
                  >
                    Register now → <ArrowRight size={15} />
                  </a>
                ) : (
                  <WebinarNotifyForm />
                )}
                <div style={{ fontSize: 11, color: '#374151', marginTop: 10 }}>
                  Free · No commitment
                </div>
              </div>
            </div>
          </div>
        )
      })()}

      {/* CTA */}
      <div
        className="gr-cta-section"
        style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' as const }}
      >
        <div
          className="gr-cta-box"
          style={{
            background: 'linear-gradient(135deg, #1a0a0a 0%, #0f0f1a 100%)',
            border: '1px solid #2d1515',
            borderRadius: 20,
          }}
        >
          <Shield size={38} color="#E63946" style={{ margin: '0 auto 18px' }} />
          <h2
            className="gr-cta-h2"
            style={{ fontWeight: 900, letterSpacing: '-1px', marginBottom: 12 }}
          >
            {isTryDomain ? T.nav_start_free : T.cta_title}
          </h2>
          <p style={{ color: '#94a3b8', fontSize: 16, marginBottom: 28, lineHeight: 1.6 }}>
            {isTryDomain ? T.hero_cta_primary : T.cta_subtitle}
          </p>

          {/* Inline lead capture */}
          <LeadCaptureForm />

          <div
            style={{
              display: 'flex',
              gap: 12,
              justifyContent: 'center',
              flexWrap: 'wrap' as const,
              marginTop: 20,
            }}
          >
            {isTryDomain ? (
              <Link
                to="/signup"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#E63946',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  padding: '14px 28px',
                  borderRadius: 10,
                  textDecoration: 'none',
                }}
              >
                Start 7-day trial — no card needed <ArrowRight size={16} />
              </Link>
            ) : (
              <Link
                to="/signup"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#E63946',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 15,
                  padding: '14px 28px',
                  borderRadius: 10,
                  textDecoration: 'none',
                }}
              >
                {T.nav_start_free} <ArrowRight size={16} />
              </Link>
            )}
            <Link
              to="/demo"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                color: '#94a3b8',
                fontWeight: 600,
                fontSize: 15,
                padding: '14px 16px',
                textDecoration: 'none',
              }}
            >
              Request Demo →
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="gr-footer-outer" style={{ borderTop: '1px solid #0f172a' }}>
        <div className="gr-footer-inner" style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 13, color: '#374151' }}>{T.footer_legal}</div>
            <a
              href="https://x.com/TryGuardRok"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4b5563', display: 'flex', alignItems: 'center' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#e2e8f0')}
              onMouseLeave={e => (e.currentTarget.style.color = '#4b5563')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://www.linkedin.com/company/guardrok/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#4b5563', display: 'flex', alignItems: 'center' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#e2e8f0')}
              onMouseLeave={e => (e.currentTarget.style.color = '#4b5563')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </a>
          </div>
          <div className="gr-footer-links">
            <Link to="/about" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              About
            </Link>
            <Link to="/privacy" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Privacy
            </Link>
            <Link to="/terms" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Terms
            </Link>
            <Link to="/cookies" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Cookies
            </Link>
            <Link
              to="/do-not-sell"
              style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}
            >
              Do Not Sell
            </Link>
            <Link to="/security" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Security
            </Link>
            <Link to="/design-partners" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Design Partners
            </Link>
            <Link to="/sitemap" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Sitemap
            </Link>
            <Link to="/support" style={{ fontSize: 13, color: '#4b5563', textDecoration: 'none' }}>
              Contact
            </Link>
          </div>
        </div>
      </div>

      <ChatWidget />

      <style>{`
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

        /* ── Base layout classes ── */
        .gr-nav               { padding: 20px 48px; }
        .gr-nav-cta-mobile    { display: none; }
        .gr-hero              { padding: 80px 48px 64px; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; }
        .gr-hero-title        { font-size: 44px; }
        .gr-section           { padding-left: 48px; padding-right: 48px; }
        .gr-pad-section       { padding: 72px 48px; }
        .gr-grid-2            { grid-template-columns: 1fr 1fr; }
        .gr-grid-3            { grid-template-columns: 1fr 1fr 1fr; }
        .gr-grid-4            { grid-template-columns: 1fr 1fr 1fr 1fr; }
        .gr-block-grid        { grid-template-columns: 1fr 1fr; gap: 64px; }
        .gr-trust-bar         { padding: 24px 48px; }
        .gr-cta-section       { padding: 0 48px 96px; }
        .gr-cta-box           { padding: 56px 48px; }
        .gr-cta-h2            { font-size: 30px; }
        .gr-footer-outer      { padding: 24px 48px; }
        .gr-footer-inner      { display: flex; align-items: center; justify-content: space-between; }
        .gr-footer-links      { display: flex; gap: 20px; flex-wrap: wrap; }

        /* ── Tablet (≤ 900px) ── */
        @media (max-width: 900px) {
          .gr-nav              { padding: 16px 28px; }
          .gr-hero             { padding: 56px 28px 48px !important; gap: 40px !important; }
          .gr-hero-title       { font-size: 36px !important; }
          .gr-section          { padding-left: 28px !important; padding-right: 28px !important; }
          .gr-pad-section      { padding: 56px 28px !important; }
          .gr-grid-3           { grid-template-columns: 1fr 1fr !important; }
          .gr-grid-4           { grid-template-columns: 1fr 1fr !important; }
          .gr-trust-bar        { padding: 20px 28px !important; }
          .gr-cta-section      { padding: 0 28px 72px !important; }
          .gr-footer-outer     { padding: 24px 28px !important; }
        }

        /* ── Mobile (≤ 640px) ── */
        @media (max-width: 640px) {
          .gr-nav              { padding: 14px 20px !important; }
          .gr-nav-links        { display: none !important; }
          .gr-nav-cta-mobile   { display: flex !important; align-items: center !important; gap: 8px !important; }

          .gr-hero             { grid-template-columns: 1fr !important; gap: 32px !important; padding: 40px 20px 40px !important; }
          .gr-hero-title       { font-size: 28px !important; letter-spacing: -1px !important; }

          .gr-section          { padding-left: 20px !important; padding-right: 20px !important; }
          .gr-pad-section      { padding: 48px 20px !important; }

          .gr-grid-2           { grid-template-columns: 1fr !important; }
          .gr-grid-3           { grid-template-columns: 1fr !important; }
          .gr-grid-4           { grid-template-columns: 1fr 1fr !important; }
          .gr-block-grid       { grid-template-columns: 1fr !important; gap: 32px !important; }

          .gr-trust-bar        { padding: 20px !important; }
          .gr-cta-section      { padding: 0 20px 64px !important; }
          .gr-cta-box          { padding: 36px 24px !important; }
          .gr-cta-h2           { font-size: 22px !important; }

          .gr-footer-outer     { padding: 24px 20px !important; }
          .gr-footer-inner     { flex-direction: column !important; align-items: flex-start !important; gap: 14px !important; }
          .gr-footer-links     { gap: 12px !important; }
        }
      `}</style>
    </div>
  )
}
